import { X, ChevronLeft, ChevronRight } from "lucide-react";
import React, { useState, useRef, useEffect } from "react";
import prescriptionImagePlaceholder from "../../../../../assets/images/prescription-image.png";

interface PrescriptionImageProps {
  imageUrls: string[] | string;
}

const PrescriptionImage: React.FC<PrescriptionImageProps> = (props) => {
  const imageUrls = Array.isArray(props.imageUrls)
    ? props.imageUrls
    : [props.imageUrls];

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleStartIndex, setVisibleStartIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const ITEMS_PER_VIEW = 4;
  const ITEM_GAP = 16;

  useEffect(() => {
    setVisibleStartIndex(0);
  }, [imageUrls]);

  const handleOpenPreview = (imageUrl: string) => {
    const encodedUrl = encodeURIComponent(imageUrl);
    const routePath = `/admin/image-preview?imagePath=${encodedUrl}`;
    window.open(routePath,'_blank');
  };

  const handleClosePreview = () => setIsPreviewOpen(false);

  const goToPrevious = () => {
    const newIndex = Math.max(0, visibleStartIndex - ITEMS_PER_VIEW);
    setVisibleStartIndex(newIndex);
    scrollToPosition(newIndex);
  };

  const goToNext = () => {
    const newIndex = Math.min(
      imageUrls.length - ITEMS_PER_VIEW,
      visibleStartIndex + ITEMS_PER_VIEW
    );
    setVisibleStartIndex(newIndex);
    scrollToPosition(newIndex);
  };

  const scrollToPosition = (index: number) => {
    if (carouselRef.current) {
      const container = carouselRef.current;
      const itemWidth = container.children[0]?.clientWidth || 0;
      const scrollPosition = index * (itemWidth + ITEM_GAP);

      container.scrollTo({
        left: scrollPosition,
        behavior: "smooth",
      });
    }
  };

  // Calculate the width for each item to ensure exactly 4 fit
  const itemWidth = `calc((100% - ${
    (ITEMS_PER_VIEW - 1) * ITEM_GAP
  }px) / ${ITEMS_PER_VIEW})`;

  return (
    <div className="mb-6">
      <p className="text-sm text-gray-600 mb-2"><span className="font-semibold">Prescription</span> : ({imageUrls.length} prescription uploaded)</p>

      {/* Carousel Container */}
      <div className="relative">
        {/* Left Arrow */}
        {visibleStartIndex > 0 && (
          <button
            onClick={goToPrevious}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-1 shadow-md"
          >
            <ChevronLeft size={24} className="text-gray-700" />
          </button>
        )}

        {/* Carousel Thumbnails */}
        <div
          ref={carouselRef}
          className="flex overflow-x-auto space-x-4 pb-2 snap-x snap-mandatory scrollbar-hide"
          style={{
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "none",
          }}
        >
          {imageUrls.map((url, index) => (
            <div
              key={index}
              className="relative flex-shrink-0 snap-start"
              style={{
                width: itemWidth,
                minWidth: itemWidth,
              }}
            >
              <img
                src={url || prescriptionImagePlaceholder}
                alt={`Prescription ${index + 1}`}
                className="w-full h-[150px] object-cover rounded-xl border border-gray-300 cursor-pointer hover:scale-[1.03] transition-transform duration-300"
                onClick={() => handleOpenPreview(url)}
              />
              <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                {index + 1}/{imageUrls.length}
              </div>
            </div>
          ))}
        </div>

        {/* Right Arrow */}
        {visibleStartIndex + ITEMS_PER_VIEW < imageUrls.length && (
          <button
            onClick={goToNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-1 shadow-md"
          >
            <ChevronRight size={24} className="text-gray-700" />
          </button>
        )}
      </div>

      {/* Pagination Dots */}
      {imageUrls.length > ITEMS_PER_VIEW && (
        <>
          <div className="flex justify-center space-x-2 mt-2">
            {Array.from({
              length: Math.ceil(imageUrls.length / ITEMS_PER_VIEW),
            }).map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setVisibleStartIndex(index * ITEMS_PER_VIEW);
                  scrollToPosition(index * ITEMS_PER_VIEW);
                }}
                className={`w-2 h-2 rounded-full ${
                  index === Math.floor(visibleStartIndex / ITEMS_PER_VIEW)
                    ? "bg-gray-700"
                    : "bg-gray-300"
                }`}
                aria-label={`Go to page ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}

      {/* Fullscreen Preview */}
      {isPreviewOpen && imageUrls.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
          <div className="relative max-w-4xl w-full">
            {imageUrls.length > 1 && (
              <>
                <button
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-30 hover:bg-opacity-50 rounded-full p-2 z-50"
                  onClick={() =>
                    setCurrentIndex((prev) =>
                      prev === 0 ? imageUrls.length - 1 : prev - 1
                    )
                  }
                >
                  <ChevronLeft size={32} className="text-white" />
                </button>
                <button
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-30 hover:bg-opacity-50 rounded-full p-2 z-50"
                  onClick={() =>
                    setCurrentIndex((prev) =>
                      prev === imageUrls.length - 1 ? 0 : prev + 1
                    )
                  }
                >
                  <ChevronRight size={32} className="text-white" />
                </button>
              </>
            )}

            <button
              className="absolute top-4 right-4 text-red-700 z-50 hover:text-gray-300"
              onClick={handleClosePreview}
            >
              <X size={28} />
            </button>

            <img
              src={imageUrls[currentIndex] || prescriptionImagePlaceholder}
              alt={`Full Prescription Preview ${currentIndex + 1}`}
              className="w-full max-h-[90vh] object-contain rounded-lg transition-transform duration-300 ease-in-out"
            />

            {imageUrls.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white bg-opacity-20 text-white px-4 py-1 rounded-full text-sm font-medium">
                {currentIndex + 1} / {imageUrls.length}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PrescriptionImage;
